// src/components/staff/StaffCertifications.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/authClient';
import {
  Award, Plus, Edit2, Trash2, Eye, X, Upload,
  Search, Filter, RefreshCw, Loader, Download, FileText, FileSpreadsheet,
  Calendar, Clock, AlertTriangle, CheckCircle, User, Badge,
  ChevronDown, ChevronRight, ExternalLink, AlertCircle, Wrench, Fuel, Heart, Shield
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Toast Notification Component ─────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-sm shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertCircle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertTriangle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Helper pour formater les erreurs
const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (error.response?.data) {
    const data = error.response.data;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
    if (Array.isArray(data)) {
      return data.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof data === 'object') {
      const errors = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errors.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          errors.push(`${key}: ${value}`);
        }
      });
      if (errors.length > 0) return errors.join('; ');
    }
  }
  if (error.message) return error.message;
  return defaultMessage;
};

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-white mt-1" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ─── Certification Type Badge ────────────────────────────────────────────────
const CertTypeBadge = ({ type }) => {
  const config = {
    pilot: { icon: Award, color: 'text-blue-400 bg-blue-500/20', label: 'Pilot' },
    a_and_p: { icon: Wrench, color: 'text-cyan-400 bg-cyan-500/20', label: 'A&P Mechanic' },
    inspection_authorization: { icon: Award, color: 'text-purple-400 bg-purple-500/20', label: 'IA' },
    fuel_handler: { icon: Fuel, color: 'text-orange-400 bg-orange-500/20', label: 'Fuel Handler' },
    safety: { icon: AlertTriangle, color: 'text-yellow-400 bg-yellow-500/20', label: 'Safety' },
    first_aid_cpr: { icon: Heart, color: 'text-red-400 bg-red-500/20', label: 'First Aid/CPR' },
    faa_driver: { icon: User, color: 'text-green-400 bg-green-500/20', label: 'FAA Driver' },
    sida_badge: { icon: Badge, color: 'text-indigo-400 bg-indigo-500/20', label: 'SIDA Badge' },
    runway_safety: { icon: AlertTriangle, color: 'text-amber-400 bg-amber-500/20', label: 'Runway Safety' },
    fod_control: { icon: AlertTriangle, color: 'text-emerald-400 bg-emerald-500/20', label: 'FOD Control' },
    aviation_security: { icon: Shield, color: 'text-slate-400 bg-slate-500/20', label: 'Aviation Security' },
    custom: { icon: Award, color: 'text-gray-400 bg-gray-500/20', label: 'Custom' }
  };
  const c = config[type] || config.custom;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${c.color}`}>
      <Icon size={12} /> {c.label}
    </span>
  );
};

// ─── Delete Modal ────────────────────────────────────────────────────────────
const DeleteModal = ({ cert, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Delete Certification</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">
            Are you sure you want to delete certification for <span className="text-white font-medium">{cert?.certification_name}</span>?
          </p>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Reason for deletion *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Explain why this certification is being deleted..."
              required
            />
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white text-sm"
          >
            Delete Certification
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Certification Form Modal ────────────────────────────────────────────────
const CertFormModal = ({ cert, onSave, onClose, staffList, showToast }) => {
  const [formData, setFormData] = useState({
    staff_id: cert?.staff_id || '',
    certification_type: cert?.certification_type || 'pilot',
    certification_name: cert?.certification_name || '',
    issuing_authority: cert?.issuing_authority || '',
    certificate_number: cert?.certificate_number || '',
    issue_date: cert?.issue_date || '',
    expiry_date: cert?.expiry_date || '',
    is_current: cert?.is_current !== undefined ? cert.is_current : true,
    badge_number: cert?.badge_number || '',
    badge_issued_at: cert?.badge_issued_at || '',
    badge_expires_at: cert?.badge_expires_at || '',
    notes: cert?.notes || ''
  });
  const [error, setError] = useState('');

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    if (!formData.staff_id || !formData.certification_name) {
      setError('Staff member and certification name are required');
      return;
    }
    setError('');
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
          <h3 className="text-white font-semibold">
            {cert ? 'Edit Certification' : 'Add Certification'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Staff Member *</label>
              <select
                value={formData.staff_id}
                onChange={(e) => updateField('staff_id', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">— Select Staff —</option>
                {staffList.map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.role})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Certification Type</label>
              <select
                value={formData.certification_type}
                onChange={(e) => updateField('certification_type', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="pilot">Pilot</option>
                <option value="a_and_p">A&P Mechanic</option>
                <option value="inspection_authorization">Inspection Authorization (IA)</option>
                <option value="fuel_handler">Fuel Handler</option>
                <option value="safety">Safety</option>
                <option value="first_aid_cpr">First Aid / CPR</option>
                <option value="faa_driver">FAA Driver</option>
                <option value="sida_badge">SIDA Badge</option>
                <option value="runway_safety">Runway Safety</option>
                <option value="fod_control">FOD Control</option>
                <option value="aviation_security">Aviation Security</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-xs block mb-1">Certification Name *</label>
            <input
              type="text"
              value={formData.certification_name}
              onChange={(e) => updateField('certification_name', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="e.g., Private Pilot License"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Issuing Authority</label>
              <input
                type="text"
                value={formData.issuing_authority}
                onChange={(e) => updateField('issuing_authority', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                placeholder="e.g., FAA"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Certificate Number</label>
              <input
                type="text"
                value={formData.certificate_number}
                onChange={(e) => updateField('certificate_number', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                placeholder="License/Certificate #"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Issue Date</label>
              <input
                type="date"
                value={formData.issue_date}
                onChange={(e) => updateField('issue_date', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Expiry Date</label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => updateField('expiry_date', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_current"
              checked={formData.is_current}
              onChange={(e) => updateField('is_current', e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <label htmlFor="is_current" className="text-gray-300 text-sm">Currently Valid</label>
          </div>
          
          <div className="border-t border-gray-700 pt-3">
            <h4 className="text-gray-400 text-xs font-medium mb-2">SIDA Badge Information (if applicable)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Badge Number</label>
                <input
                  type="text"
                  value={formData.badge_number}
                  onChange={(e) => updateField('badge_number', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="Badge ID"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Badge Issued</label>
                <input
                  type="datetime-local"
                  value={formData.badge_issued_at?.slice(0, 16) || ''}
                  onChange={(e) => updateField('badge_issued_at', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Badge Expires</label>
                <input
                  type="datetime-local"
                  value={formData.badge_expires_at?.slice(0, 16) || ''}
                  onChange={(e) => updateField('badge_expires_at', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>
          </div>
          
          <div>
            <label className="text-gray-400 text-xs block mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              placeholder="Additional notes..."
            />
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm">
            {cert ? 'Update' : 'Create'} Certification
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── File Upload Modal ───────────────────────────────────────────────────────
const FileUploadModal = ({ cert, onUpload, onClose, showToast }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/staff/certifications/${cert.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('File uploaded successfully!', 'success');
      onUpload();
      onClose();
    } catch (error) {
      showToast(formatErrorMessage(error, 'Failed to upload file'), 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Upload Certificate File</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-300 text-sm">Upload PDF or image file for: <span className="text-white">{cert?.certification_name}</span></p>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files[0])}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
          />
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const StaffCertifications = () => {
  const [certifications, setCertifications] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [deletingCert, setDeletingCert] = useState(null);
  const [uploadingForCert, setUploadingForCert] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState(null);

  const [stats, setStats] = useState({ total: 0, current: 0, expiring30: 0, expired: 0 });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await api.get('/company/settings');
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) setLogoUrl(`${API_BASE}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [certsRes, staffRes] = await Promise.all([
        api.get('/staff/certifications/'),
        api.get('/staff/')
      ]);
      
      setCertifications(certsRes.data || []);
      setStaffList(staffRes.data || []);
      
      const now = new Date();
      const thirtyDaysLater = new Date();
      thirtyDaysLater.setDate(now.getDate() + 30);
      
      const current = (certsRes.data || []).filter(c => c.is_current).length;
      const expiring30 = (certsRes.data || []).filter(c => {
        if (!c.expiry_date || !c.is_current) return false;
        const expiry = new Date(c.expiry_date);
        return expiry <= thirtyDaysLater && expiry > now;
      }).length;
      const expired = (certsRes.data || []).filter(c => {
        if (!c.expiry_date || !c.is_current) return false;
        return new Date(c.expiry_date) < now;
      }).length;
      
      setStats({ total: (certsRes.data || []).length, current, expiring30, expired });
    } catch (error) {
      showToast(formatErrorMessage(error, 'Error fetching data'), 'error');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => {
    fetchCompanySettings();
    fetchData();
  }, [fetchData, fetchCompanySettings]);

  const filteredCerts = certifications.filter(cert => {
    const staff = staffList.find(s => s.id === cert.staff_id);
    const staffName = staff ? `${staff.first_name} ${staff.last_name}` : '';
    const matchesSearch = 
      cert.certification_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staffName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || cert.certification_type === typeFilter;
    
    if (expiringOnly && cert.expiry_date && cert.is_current) {
      const expiry = new Date(cert.expiry_date);
      const thirtyDaysLater = new Date();
      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
      return matchesSearch && matchesType && expiry <= thirtyDaysLater;
    }
    
    return matchesSearch && matchesType;
  });

  const handleSaveCert = async (formData) => {
    try {
      if (editingCert) {
        await api.put(`/staff/certifications/${editingCert.id}`, formData);
        showToast(`Certification "${formData.certification_name}" updated successfully`, 'success');
      } else {
        await api.post('/staff/certifications/', formData);
        showToast(`Certification "${formData.certification_name}" created successfully`, 'success');
      }
      await fetchData();
      setShowForm(false);
      setEditingCert(null);
    } catch (error) {
      showToast(formatErrorMessage(error, 'Error saving certification'), 'error');
    }
  };

  const handleDeleteCert = async (reason) => {
    try {
      await api.delete(`/staff/certifications/${deletingCert.id}`, { data: { reason } });
      showToast(`Certification "${deletingCert.certification_name}" deleted successfully`, 'success');
      await fetchData();
      setDeletingCert(null);
    } catch (error) {
      showToast(formatErrorMessage(error, 'Error deleting certification'), 'error');
    }
  };

  const getStaffName = (staffId) => {
    const staff = staffList.find(s => s.id === staffId);
    return staff ? `${staff.first_name} ${staff.last_name}` : 'Unknown';
  };

  const isExpiringSoon = (expiryDate, isCurrent) => {
    if (!expiryDate || !isCurrent) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const thirtyDaysLater = new Date();
    thirtyDaysLater.setDate(now.getDate() + 30);
    return expiry <= thirtyDaysLater && expiry > now;
  };

  const isExpired = (expiryDate, isCurrent) => {
    if (!expiryDate || !isCurrent) return false;
    return new Date(expiryDate) < new Date();
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredCerts.map(cert => ({
        'Staff': getStaffName(cert.staff_id),
        'Certification': cert.certification_name,
        'Type': cert.certification_type,
        'Number': cert.certificate_number || 'N/A',
        'Issued By': cert.issuing_authority || 'N/A',
        'Issue Date': cert.issue_date || 'N/A',
        'Expiry Date': cert.expiry_date || 'N/A',
        'Current': cert.is_current ? 'Yes' : 'No',
        'Badge #': cert.badge_number || 'N/A'
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Staff Certifications');
      XLSX.writeFile(wb, `staff_certifications_${new Date().toISOString().slice(0,19)}.xlsx`);
      showToast('Excel export completed', 'success');
    } catch (error) {
      showToast('Error exporting to Excel', 'error');
    }
  };

  const exportToPDF = async () => {
    try {
      const doc = new jsPDF();
      const companyName = company?.name || 'AeroTrick FBO';
      
      if (logoUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.src = logoUrl;
          await new Promise((resolve) => {
            img.onload = () => { doc.addImage(img, 'PNG', 14, 10, 40, 15); resolve(); };
            img.onerror = resolve;
          });
        } catch (e) {}
      }
      
      doc.setFontSize(18);
      doc.setTextColor(30, 64, 175);
      doc.text('Staff Certifications Report', 70, 20);
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total: ${stats.total} | Current: ${stats.current} | Expiring Soon: ${stats.expiring30}`, 14, 45);
      
      const tableData = filteredCerts.map(cert => [
        getStaffName(cert.staff_id),
        cert.certification_name?.slice(0, 25) || '',
        cert.certification_type,
        cert.expiry_date || 'N/A',
        cert.is_current ? 'Current' : 'Expired'
      ]);
      
      doc.autoTable({
        startY: 55,
        head: [['Staff', 'Certification', 'Type', 'Expiry', 'Status']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14, right: 14 }
      });
      
      const finalY = doc.lastAutoTable?.finalY || 250;
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Generated by AeroTrick for ${companyName} · © 2026 AeroTrick. All rights reserved.`,
        14, finalY + 20
      );
      
      doc.save(`staff_certifications_${new Date().toISOString().slice(0,19)}.pdf`);
      showToast('PDF export completed', 'success');
    } catch (error) {
      showToast('Error exporting to PDF', 'error');
    }
  };

  return (
    <div className="space-y-4 px-1">
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain"
              onError={(e) => e.target.style.display = 'none'} />
          )}
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <Award size={18} className="text-blue-400" />
              </div>
              Staff Certifications
            </h2>
            <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
              <Clock size={11} />
              Last refresh: {lastRefresh.toLocaleTimeString()} — FAA, SIDA, and other certifications
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportToExcel} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs">
            <FileSpreadsheet size={12} /> Excel
          </button>
          <button onClick={exportToPDF} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 text-xs">
            <FileText size={12} /> PDF
          </button>
          <button onClick={fetchData} className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Total Certifications" value={stats.total} icon={Award} color="#38bdf8" />
        <StatCard title="Current" value={stats.current} icon={CheckCircle} color="#10b981" />
        <StatCard title="Expiring in 30 Days" value={stats.expiring30} icon={AlertTriangle} color="#f59e0b" />
        <StatCard title="Expired" value={stats.expired} icon={AlertCircle} color="#ef4444" />
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by staff, certification, or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-4 text-white text-sm"
          />
        </div>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Types</option>
          <option value="pilot">Pilot</option>
          <option value="a_and_p">A&P Mechanic</option>
          <option value="fuel_handler">Fuel Handler</option>
          <option value="sida_badge">SIDA Badge</option>
          <option value="safety">Safety</option>
        </select>
        
        <label className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={expiringOnly}
            onChange={(e) => setExpiringOnly(e.target.checked)}
            className="w-4 h-4 accent-yellow-500"
          />
          Show expiring soon only
        </label>
        
        <button
          onClick={() => { setEditingCert(null); setShowForm(true); }}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm"
        >
          <Plus size={14} /> Add Certification
        </button>
      </div>
      
      {/* Certifications Table */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Staff</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Certification</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Type</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Number</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Expiry</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-12 text-gray-500"><Loader className="animate-spin mx-auto" /></td></tr>
              ) : filteredCerts.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-gray-500">No certifications found</td></tr>
              ) : (
                filteredCerts.map((cert) => {
                  const expiring = isExpiringSoon(cert.expiry_date, cert.is_current);
                  const expired = isExpired(cert.expiry_date, cert.is_current);
                  return (
                    <React.Fragment key={cert.id}>
                      <tr className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                          onClick={() => setExpandedId(expandedId === cert.id ? null : cert.id)}>
                        <td className="px-4 py-3 text-gray-300 text-sm">{getStaffName(cert.staff_id)}</td>
                        <td className="px-4 py-3 text-white text-sm font-medium">{cert.certification_name}</td>
                        <td className="px-4 py-3"><CertTypeBadge type={cert.certification_type} /></td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono">{cert.certificate_number || '—'}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {cert.expiry_date ? (
                            <span className={expiring ? 'text-yellow-400' : expired ? 'text-red-400' : ''}>
                              {new Date(cert.expiry_date).toLocaleDateString()}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {cert.is_current ? (
                            <span className={`text-xs ${expiring ? 'text-yellow-400' : expired ? 'text-red-400' : 'text-green-400'}`}>
                              {expiring ? 'Expiring Soon' : expired ? 'Expired' : 'Current'}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={(e) => { e.stopPropagation(); setUploadingForCert(cert); }}
                              className="p-1 hover:bg-gray-600 rounded text-purple-400" title="Upload file">
                              <Upload size={14} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setEditingCert(cert); setShowForm(true); }}
                              className="p-1 hover:bg-gray-600 rounded text-yellow-400" title="Edit">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setDeletingCert(cert); }}
                              className="p-1 hover:bg-gray-600 rounded text-red-400" title="Delete">
                              <Trash2 size={14} />
                            </button>
                            {expandedId === cert.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </div>
                        </td>
                      </tr>
                      {expandedId === cert.id && (
                        <tr className="bg-gray-800/40">
                          <td colSpan="7" className="px-4 py-4">
                            <div className="space-y-2">
                              {cert.issuing_authority && (
                                <p className="text-gray-300 text-sm">
                                  <span className="text-gray-500">Issued by:</span> {cert.issuing_authority}
                                </p>
                              )}
                              {cert.issue_date && (
                                <p className="text-gray-300 text-sm">
                                  <span className="text-gray-500">Issue date:</span> {new Date(cert.issue_date).toLocaleDateString()}
                                </p>
                              )}
                              {cert.badge_number && (
                                <p className="text-gray-300 text-sm">
                                  <span className="text-gray-500">Badge #:</span> {cert.badge_number}
                                  {cert.badge_expires_at && ` (expires ${new Date(cert.badge_expires_at).toLocaleDateString()})`}
                                </p>
                              )}
                              {cert.notes && (
                                <p className="text-gray-300 text-sm">
                                  <span className="text-gray-500">Notes:</span> {cert.notes}
                                </p>
                              )}
                              {cert.file_url && (
                                <a href={`${API_BASE}${cert.file_url}`} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-400 text-xs hover:underline">
                                  <ExternalLink size={12} /> View Certificate File
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-gray-500 text-xs">
          Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
        </p>
      </div>
      
      {/* Modals */}
      {showForm && (
        <CertFormModal
          cert={editingCert}
          onSave={handleSaveCert}
          onClose={() => { setShowForm(false); setEditingCert(null); }}
          staffList={staffList}
          showToast={showToast}
        />
      )}
      
      {deletingCert && (
        <DeleteModal
          cert={deletingCert}
          onConfirm={handleDeleteCert}
          onClose={() => setDeletingCert(null)}
        />
      )}
      
      {uploadingForCert && (
        <FileUploadModal
          cert={uploadingForCert}
          onUpload={fetchData}
          onClose={() => setUploadingForCert(null)}
          showToast={showToast}
        />
      )}
      
      {/* Toast Notification */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
    </div>
  );
};

export default StaffCertifications;